import React from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, MapPin, Camera, Lightbulb, Mail, Compass } from "lucide-react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import Chatbot from "@/components/Chatbot";
import LazyVideo from "@/components/LazyVideo";
import locations, { categories } from "@/data/locations";

const R2 = "https://pub-626563a90a7f431c96f87c5838c120bc.r2.dev";

const LocationPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const loc = locations.find((l) => l.id === slug);

  if (!loc) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-center p-8">
        <h1 className="text-3xl font-bold mb-4">Drehort nicht gefunden</h1>
        <button
          onClick={() => navigate("/#drehorte")}
          className="px-6 py-3 bg-[hsl(var(--accent))] rounded-xl text-white font-semibold"
        >
          Zur Drehorte-Karte
        </button>
      </div>
    );
  }

  const cat = categories[loc.category];
  const canonical = `https://freysmedia.de/drehort/${loc.id}`;
  const title = `Drehort ${loc.name} · Videoproduktion in ${loc.region || "Baden-Württemberg"} | Freys Media`;
  const description = loc.description
    ? loc.description.slice(0, 155).replace(/\n/g, " ")
    : `Videodreh in ${loc.name}: ${loc.projects.join(", ")} — Kenny Frey von Freys Media filmt in ${loc.region || "Baden-Württemberg"}.`;

  // OSM embed bbox
  const bbox = [loc.lon - 0.01, loc.lat - 0.005, loc.lon + 0.01, loc.lat + 0.005].join(",");
  const osmSrc = `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${loc.lat}%2C${loc.lon}`;

  // Schema.org structured data
  const placeSchema = {
    "@context": "https://schema.org",
    "@type": "Place",
    name: `${loc.name} — Drehort von Freys Media`,
    description: loc.description || `Videoproduktion in ${loc.name}.`,
    address: {
      "@type": "PostalAddress",
      addressLocality: loc.name,
      addressRegion: loc.region || "Baden-Württemberg",
      addressCountry: "DE",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: loc.lat,
      longitude: loc.lon,
    },
    url: canonical,
    image: loc.poster ? `https://freysmedia.de${loc.poster}` : undefined,
  };

  const videoSchemas = (loc.relatedVideos || []).map((url) => ({
    "@context": "https://schema.org",
    "@type": "VideoObject",
    name: `${loc.name} — Videoproduktion`,
    description: `Video-Produktion gedreht in ${loc.name} von Freys Media`,
    contentUrl: url,
    thumbnailUrl: loc.poster ? `https://freysmedia.de${loc.poster}` : undefined,
    uploadDate: "2025-01-01",
    locationCreated: {
      "@type": "Place",
      name: loc.name,
      address: { "@type": "PostalAddress", addressLocality: loc.name, addressCountry: "DE" },
    },
  }));

  return (
    <div className="min-h-screen relative">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonical} />
        {loc.poster && <meta property="og:image" content={`https://freysmedia.de${loc.poster}`} />}
        <meta name="geo.region" content="DE-BW" />
        <meta name="geo.placename" content={loc.name} />
        <meta name="geo.position" content={`${loc.lat};${loc.lon}`} />
        <meta name="ICBM" content={`${loc.lat}, ${loc.lon}`} />
        <script type="application/ld+json">{JSON.stringify(placeSchema)}</script>
        {videoSchemas.map((s, i) => (
          <script key={i} type="application/ld+json">
            {JSON.stringify(s)}
          </script>
        ))}
      </Helmet>

      <Navigation />

      {/* Hero image background */}
      {loc.heroImage && (
        <div className="absolute top-0 left-0 right-0 h-[420px] sm:h-[520px] -z-10 overflow-hidden">
          <img
            src={loc.heroImage}
            alt={`${loc.name} — Drehort von oben`}
            className="w-full h-full object-cover"
            loading="eager"
            decoding="async"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/60 via-background/40 to-background" />
        </div>
      )}

      {/* Back link */}
      <div className={`${loc.heroImage ? "pt-24" : "pt-24"} pb-2 container mx-auto px-4 sm:px-6 lg:px-8`}>
        <button
          onClick={() => navigate("/#drehorte")}
          className="flex items-center gap-2 text-muted-foreground hover:text-white transition-colors text-sm"
        >
          <ArrowLeft className="w-4 h-4" /> Zurück zur Drehorte-Karte
        </button>
      </div>

      {/* Hero */}
      <section className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center gap-2 mb-3">
          {cat && (
            <span
              className="px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-widest"
              style={{
                backgroundColor: `${cat.color}22`,
                color: cat.color,
                border: `1px solid ${cat.color}66`,
              }}
            >
              {cat.label}
            </span>
          )}
          {loc.home && (
            <span className="px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-widest bg-[hsl(var(--accent))]/20 text-[hsl(var(--accent))] border border-[hsl(var(--accent))]/40">
              Home Base
            </span>
          )}
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-3" data-testid="loc-title">
          Drehort {loc.name}
        </h1>
        <div className="flex flex-wrap items-center gap-4 text-muted-foreground text-sm">
          {loc.address && (
            <span className="flex items-center gap-1.5">
              <MapPin className="w-4 h-4" /> {loc.address}
            </span>
          )}
          <span className="flex items-center gap-1.5">
            <Compass className="w-4 h-4" /> {loc.lat.toFixed(4)}°N · {loc.lon.toFixed(4)}°E
          </span>
        </div>
      </section>

      {/* Hero video */}
      {loc.video && (
        <section className="container mx-auto px-4 sm:px-6 lg:px-8 mb-12">
          <div className={loc.aspect === "9/16" ? "max-w-md mx-auto" : "max-w-5xl mx-auto"}>
            <div className="rounded-2xl overflow-hidden border border-white/5">
              <LazyVideo
                src={loc.video}
                poster={loc.poster}
                hoverToPlay={true}
                aspectRatio={loc.aspect || "16/9"}
                className="bg-secondary"
              />
            </div>
          </div>
        </section>
      )}

      {/* Description */}
      {loc.description && (
        <section className="container mx-auto px-4 sm:px-6 lg:px-8 mb-16 max-w-3xl">
          <h2 className="text-2xl font-bold mb-4">Über den Drehort</h2>
          <p className="text-lg text-muted-foreground leading-relaxed whitespace-pre-line">
            {loc.description}
          </p>
        </section>
      )}

      {/* Geeignet für (customer perspective) */}
      {loc.bestFor && loc.bestFor.length > 0 && (
        <section className="container mx-auto px-4 sm:px-6 lg:px-8 mb-16 max-w-4xl">
          <h2 className="text-2xl font-bold mb-4">Geeignet für Projekte wie</h2>
          <div className="flex flex-wrap gap-2">
            {loc.bestFor.map((b) => (
              <span
                key={b}
                className="px-4 py-2 rounded-full text-sm bg-secondary/60 border border-white/10"
              >
                {b}
              </span>
            ))}
          </div>
        </section>
      )}

      {/* Gallery */}
      {loc.galleryImages && loc.galleryImages.length > 0 && (
        <section className="container mx-auto px-4 sm:px-6 lg:px-8 mb-16">
          <h2 className="text-2xl font-bold mb-6">Bilder vom Drehort</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
            {loc.galleryImages.map((img, i) => (
              <a
                key={i}
                href={img}
                target="_blank"
                rel="noopener noreferrer"
                className="group relative overflow-hidden rounded-xl border border-white/5 bg-secondary/50 aspect-[3/2]"
              >
                <img
                  src={img}
                  alt={`${loc.name} — Foto ${i + 1}`}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </a>
            ))}
          </div>
        </section>
      )}

      {/* Related videos */}
      {loc.relatedVideos && loc.relatedVideos.length > 1 && (
        <section className="container mx-auto px-4 sm:px-6 lg:px-8 mb-16">
          <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
            <Camera className="w-6 h-6 text-[hsl(var(--accent))]" />
            Hier gedrehte Videos
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {loc.relatedVideos.map((v, i) => {
              // Derive per-video poster from URL filename: /videos/foo.mp4 -> /thumbnails/foo.webp
              const m = v.match(/\/videos\/([^/?#]+)\.mp4/);
              const videoPoster = m ? `/thumbnails/${m[1]}.webp` : loc.poster;
              return (
                <div
                  key={i}
                  className="rounded-xl overflow-hidden bg-secondary card-glow"
                >
                  <LazyVideo
                    src={v}
                    poster={videoPoster}
                    hoverToPlay={true}
                    aspectRatio={loc.aspect || "9/16"}
                  />
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Mini map */}
      <section className="container mx-auto px-4 sm:px-6 lg:px-8 mb-16">
        <h2 className="text-2xl font-bold mb-4">Lage</h2>
        <div className="rounded-2xl overflow-hidden border border-white/10 bg-secondary/30">
          <iframe
            title={`Karte ${loc.name}`}
            src={osmSrc}
            className="w-full h-[360px]"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
          <div className="p-4 text-sm text-muted-foreground flex flex-wrap gap-3 justify-between">
            <span>{loc.address || `${loc.name}, ${loc.region || "Baden-Württemberg"}`}</span>
            <a
              href={`https://www.openstreetmap.org/?mlat=${loc.lat}&mlon=${loc.lon}#map=14/${loc.lat}/${loc.lon}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[hsl(var(--accent))] hover:underline"
            >
              Auf OpenStreetMap öffnen →
            </a>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="container mx-auto px-4 sm:px-6 lg:px-8 mb-20">
        <div className="max-w-3xl mx-auto rounded-2xl bg-gradient-to-br from-[hsl(var(--accent))]/15 to-background/20 border border-[hsl(var(--accent))]/30 p-8 sm:p-10 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold mb-3">
            Auch in {loc.name} drehen?
          </h2>
          <p className="text-muted-foreground mb-6">
            Ob Imagefilm, Reel oder Immobilienvideo: Ich kenne die Region und plane den Dreh effizient.
          </p>
          <button
            onClick={() => navigate("/#contact")}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 text-white font-semibold transition-all hover:scale-[1.03]"
          >
            <Mail className="w-4 h-4" /> Anfrage starten
          </button>
        </div>
      </section>

      {/* Other locations */}
      <section className="container mx-auto px-4 sm:px-6 lg:px-8 mb-20">
        <h2 className="text-2xl font-bold mb-6">Weitere Drehorte</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {locations
            .filter((l) => l.id !== loc.id)
            .slice(0, 8)
            .map((l) => (
              <Link
                key={l.id}
                to={`/drehort/${l.id}`}
                className="p-4 rounded-xl border border-white/5 hover:border-[hsl(var(--accent))]/40 bg-secondary/40 hover:bg-secondary/60 transition-colors"
              >
                <div className="flex items-center gap-2 mb-2">
                  <span
                    className="inline-block w-2 h-2 rounded-full"
                    style={{ backgroundColor: categories[l.category]?.color }}
                  />
                  <span className="font-medium">{l.name}</span>
                </div>
                <div className="text-xs text-muted-foreground line-clamp-2">
                  {l.projects[0]}
                </div>
              </Link>
            ))}
        </div>
      </section>

      <Footer />
      <Chatbot />
    </div>
  );
};

export default LocationPage;
